import { CommonModule, DatePipe, formatDate } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  QueryList,
  signal,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FilterChipsComponent } from '@components/data-grid/filter/filter-chips/filter-chips.component';
import { FilterHelper } from '@components/data-grid/filter/filter.helper';
import { LookupSelectComponent } from '@components/forms/lookup-select/lookup-select.component';
import {
  MOTIF_DATE_TIME_FORMATS,
  MOTIF_MODAL_DATA,
  MotifBadgeModule,
  MotifButtonModule,
  MotifChipModule,
  MotifDatePickerModule,
  MotifFormsModule,
  MotifIconModule,
  MotifModalModule,
  MotifModalRef,
  MotifNativeDateTimeModule,
  MotifTooltipModule,
} from '@ey-xd/ng-motif';
import {
  AccessFilterItem,
  ConditionPropertyOperator,
  DATE_TIME_FORMAT,
  FieldType,
  FilterCriterion,
  FilterDropdownOption,
  FilterGroup,
  FilterModalData,
  FilterModalResult,
  JSONValue,
  PropertyFilter,
} from '@models';
import { TranslateModule } from '@ngx-translate/core';
import { EytcAuthService } from '@services';

@Component({
  selector: 'app-filter',
  standalone: true,
  imports: [
    CommonModule,
    MotifFormsModule,
    ReactiveFormsModule,
    FormsModule,
    MotifDatePickerModule,
    MotifNativeDateTimeModule,
    TranslateModule,
    MotifButtonModule,
    MotifIconModule,
    MotifModalModule,
    MotifBadgeModule,
    MotifTooltipModule,
    FilterChipsComponent,
    MotifChipModule,
    LookupSelectComponent,
  ],
  providers: [
    DatePipe,
    {
      provide: MOTIF_DATE_TIME_FORMATS,
      useValue: {
        ...MOTIF_DATE_TIME_FORMATS,
        datePickerInput: { year: 'numeric', month: 'short', day: '2-digit' },
        monthYearHeaderLabel: {
          year: 'numeric',
          month: 'long',
        },
      },
    },
  ],
  templateUrl: './filter.component.html',
  styleUrl: './filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterComponent implements OnInit, AfterViewInit {
  public dialogRef = inject(MotifModalRef<FilterComponent, boolean>);
  public filterModalData: FilterModalData = inject(MOTIF_MODAL_DATA);
  public form!: FormGroup;
  public FieldType = FieldType;
  chipsFilters = signal<PropertyFilter[]>([]);
  isFormUnchanged = true;

  #destroyRef: DestroyRef = inject(DestroyRef);
  #datePipe: DatePipe = inject(DatePipe);
  #initialFormValues!: Record<string, JSONValue>;
  #authService = inject(EytcAuthService);

  @ViewChildren('dateInput') dateInputs: QueryList<ElementRef> | undefined;

  areFormValuesUnchanged(): boolean {
    return JSON.stringify(this.form.getRawValue()) === JSON.stringify(this.#initialFormValues);
  }

  #minDate: Date | undefined = undefined;
  #maxDate: Date | undefined = undefined;

  getMinDate = (filterCriterion: FilterCriterion): Date | undefined => {
    return filterCriterion.dateOptions?.minDateField
      ? (this.form.get(filterCriterion.dateOptions.minDateField)?.value ?? this.#minDate)
      : this.#minDate;
  };

  getMaxDate = (filterCriterion: FilterCriterion): Date | undefined =>
    filterCriterion.dateOptions?.maxDateField
      ? (this.form.get(filterCriterion.dateOptions.maxDateField)?.value ?? this.#maxDate)
      : this.#maxDate;

  ngOnInit() {
    const group: Record<string, FormControl> = {};
    this.filterModalData.filterCriteria.forEach((filterCriterion: FilterCriterion) => {
      const initialValues: JSONValue[] = [];
      this.filterModalData.selectedValues?.forEach(group => {
        const filterValue: PropertyFilter[] =
          group.entityPropertyFilters?.filter((selectedValue: PropertyFilter) => {
            return selectedValue?.propertyName === filterCriterion.key;
          }) || [];
        if (filterValue.length > 0) {
          if (filterCriterion.type === FieldType.MultipleSelect) {
            initialValues.push(...filterValue.map((value: PropertyFilter): JSONValue => value.value));
          } else if (filterCriterion.type === FieldType.MultipleLookup) {
            initialValues.push(
              ...filterValue.map(
                (value: PropertyFilter): JSONValue =>
                  this.#toLookupOption(value.value, filterCriterion) as unknown as JSONValue
              )
            );
          } else if (filterCriterion.type === FieldType.Select) {
            initialValues.push(filterValue[0]?.value);
          } else if (filterCriterion.type === FieldType.Date) {
            initialValues.push(
              ...filterValue.map(
                (value: PropertyFilter): JSONValue =>
                  value.value &&
                  (this.#datePipe.transform(value.value as string, DATE_TIME_FORMAT.DATE_FILTER_FORMAT) as JSONValue)
              )
            );
          } else if (filterCriterion.type === FieldType.Lookup) {
            initialValues.push(this.#toLookupOption(filterValue[0]?.value, filterCriterion) as unknown as JSONValue);
          } else {
            const rawValue = filterValue[0]?.value;
            if (typeof rawValue === 'string' && rawValue.length > 0) {
              initialValues.push(rawValue.replace(/^%|%$/g, ''));
            } else if (typeof rawValue === 'number') {
              initialValues.push(rawValue);
            }
          }
        }
      });

      group[filterCriterion.key] = new FormControl(
        {
          value:
            filterCriterion.type === FieldType.MultipleSelect || filterCriterion.type === FieldType.MultipleLookup
              ? initialValues
              : filterCriterion.type === FieldType.Date
                ? this.#getDateOrValue(initialValues[0] as string)
                : initialValues[0] || '',
          disabled: false,
        },
        filterCriterion.validators || []
      );
    });
    this.form = new FormGroup(group);
    this.#initialFormValues = this.form.getRawValue();
    this.form.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.isFormUnchanged = this.areFormValuesUnchanged();
      this.#setFilterChips();
    });
    this.#setFilterChips();
  }

  onSubmit(form: FormGroup): void {
    this.dialogRef.close(this.#getAppliedFiltersWithValues(form));
  }

  onClearAll(): void {
    this.form.reset();
    this.isFormUnchanged = this.areFormValuesUnchanged();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onRemoveChip($event: PropertyFilter[]) {
    const [filter] = $event;
    this.filterModalData.filterCriteria.forEach((filterCriterion: FilterCriterion): void => {
      if (filterCriterion.key === filter.propertyName) {
        const control = this.form.get(filterCriterion.key) as AbstractControl;
        if (control) {
          if (filterCriterion.type === FieldType.MultipleSelect || filterCriterion.type === FieldType.MultipleLookup) {
            const values = control.value.filter((value: JSONValue): boolean => value !== filter.value);
            control.setValue(values);
          } else {
            control.setValue('');
          }
        }
      }
    });
  }

  hasAccess(key: string) {
    const filterCriterion = this.filterModalData.filterCriteria.find(
      (criterion: FilterCriterion): boolean => criterion.key === key
    ) as FilterCriterion;
    if (filterCriterion?.access) {
      let result = true;
      filterCriterion.access.forEach((access: AccessFilterItem): void => {
        if (access.fieldName === filterCriterion.key) {
          const hideFor: number[] = access.hideFor || [];
          if (hideFor && hideFor.length > 0 && hideFor.some(role => this.#authService.userRoles().includes(role))) {
            result = false;
          }
          const showFor: number[] = access.showFor || [];
          if (showFor && showFor.length > 0 && !showFor.some(role => this.#authService.userRoles().includes(role))) {
            result = false;
          }
        }
      });
      return result;
    } else {
      return true;
    }
  }

  dropdownOptionsSelectComparator = (a: JSONValue, b: JSONValue) => {
    let optionA: FilterDropdownOption | null;
    let optionB: FilterDropdownOption | null;
    this.filterModalData?.filterCriteria.forEach((filterCriterion: FilterCriterion): void => {
      if (Array.isArray(filterCriterion.options)) {
        optionA =
          filterCriterion.options.find(
            (option: FilterDropdownOption): boolean => option.value === (a as FilterDropdownOption | JSONValue)
          ) || null;
        optionB =
          filterCriterion.options.find(
            (option: FilterDropdownOption): boolean => option.value === (b as FilterDropdownOption | JSONValue)
          ) || null;

        if (optionA && optionB) {
          a = optionA.id;
          b = optionB.id;
        }
      }
    });
    return Number(a) - Number(b);
  };

  #getAppliedFiltersWithValues(form: FormGroup): FilterModalResult[] {
    return Object.entries(form.controls)
      .filter(
        ([key, control]) =>
          control.value && this.filterModalData.filterCriteria.some(filterCriterion => filterCriterion.key === key)
      )
      .map<FilterModalResult>(([key, control]) => {
        const controlValues = Array.isArray(control.value) ? control.value : [control.value];
        const criterion = this.filterModalData.filterCriteria.find(filterCriterion => filterCriterion.key === key);

        return {
          key,
          values: controlValues,
          operation: criterion?.operation || ConditionPropertyOperator.Equal,
          type: criterion?.type,
          field: criterion?.field,
        };
      });
  }

  #setFilterChips(): void {
    const chipsFilters: PropertyFilter[] = [];
    const filters: FilterGroup[] = FilterHelper.fromModalResultsToFilterGroups(
      this.#getAppliedFiltersWithValues(this.form)
    );
    filters.forEach((filter: FilterGroup): void => {
      chipsFilters.push(...filter.entityPropertyFilters);
    });
    this.chipsFilters.set(chipsFilters);
  }

  #toLookupOption(value: JSONValue, filterCriterion: FilterCriterion): FilterDropdownOption {
    if (typeof value === 'object' && value !== null && !Array.isArray(value) && 'value' in value) {
      return value as unknown as FilterDropdownOption;
    }

    const lookupValue = String(value ?? '');
    return (
      filterCriterion.options?.find(option => option.value === lookupValue) ?? {
        id: null,
        displayName: lookupValue,
        value: lookupValue,
      }
    );
  }

  #getDateOrValue(value: JSONValue): JSONValue | Date {
    const date: JSONValue = new Date(value as string);
    return !isNaN(date.getTime()) ? date : value;
  }

  dateTimeInput($event: { input: HTMLInputElement }): void {
    const date = new Date($event.input.value);
    if (!isNaN(date.getTime())) {
      $event.input.value = formatDate(date, DATE_TIME_FORMAT.DATE_FILTER_FORMAT, 'en-US');
    }
  }

  #formatDateInput(input: HTMLInputElement): void {
    const date = new Date(input.value);
    if (!isNaN(date.getTime())) {
      input.value = formatDate(date, DATE_TIME_FORMAT.DATE_FILTER_FORMAT, 'en-US');
    }
  }

  ngAfterViewInit(): void {
    this.dateInputs?.forEach((inputRef): void => {
      this.#formatDateInput(inputRef.nativeElement);
    });
  }
}
